import { FC, useState } from "react";
import { Button, Input, Select, Card, Tag, Empty, Spin, Modal, Form, message } from "antd";
import { Pin, Plus, Edit2, Trash2, StickyNote } from "lucide-react";
import { useMissionControlStore } from "../../mission-control.store";
import {
  useGetProjectNotes,
  useCreateProjectNote,
  useUpdateProjectNote,
  useDeleteProjectNote,
  useToggleProjectNotePin,
} from "~/services/project-notes/project-notes.queries";
import { CreateProjectNoteDto, UpdateProjectNoteDto } from "~/services/project-notes/project-notes.services";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { TextArea } = Input;

type Props = {};

export const NotesPanel: FC<Props> = () => {
  const { selectedProjectId } = useMissionControlStore();
  const [noteTypeFilter, setNoteTypeFilter] = useState<string | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<any | null>(null);
  const [form] = Form.useForm();

  const { data: notesData, isLoading } = useGetProjectNotes({
    projectId: selectedProjectId || undefined,
    noteType: noteTypeFilter,
    pageSize: 100,
  });

  const createMutation = useCreateProjectNote();
  const updateMutation = useUpdateProjectNote();
  const deleteMutation = useDeleteProjectNote();
  const togglePinMutation = useToggleProjectNotePin();

  const handleOpenModal = (note?: any) => {
    if (note) {
      setEditingNote(note);
      form.setFieldsValue(note);
    } else {
      setEditingNote(null);
      form.resetFields();
      form.setFieldValue("projectId", selectedProjectId);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNote(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingNote) {
        await updateMutation.mutateAsync({
          id: editingNote.id,
          data: values as UpdateProjectNoteDto,
        });
        message.success("Note updated successfully");
      } else {
        await createMutation.mutateAsync(values as CreateProjectNoteDto);
        message.success("Note created successfully");
      }

      handleCloseModal();
    } catch (error) {
      console.error("Failed to save note:", error);
      message.error("Failed to save note");
    }
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: "Delete Note",
      content: "Are you sure you want to delete this note?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(id);
          message.success("Note deleted successfully");
        } catch (error) {
          message.error("Failed to delete note");
        }
      },
    });
  };

  const handleTogglePin = async (id: string) => {
    try {
      await togglePinMutation.mutateAsync(id);
    } catch (error) {
      message.error("Failed to toggle pin");
    }
  };

  const getNoteTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      general: "default",
      operational: "blue",
      client: "green",
      internal: "purple",
    };
    return colors[type] || "default";
  };

  if (!selectedProjectId) {
    return null;
  }

  const notes = notesData?.items || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <StickyNote className="text-yellow-600" size={24} />
          <div>
            <h2 className="text-xl font-semibold">Project Notes</h2>
            <p className="text-sm text-gray-500">
              {notes.length} note{notes.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Select
            placeholder="Filter by type"
            style={{ width: 150 }}
            allowClear
            value={noteTypeFilter}
            onChange={setNoteTypeFilter}
            options={[
              { label: "General", value: "general" },
              { label: "Operational", value: "operational" },
              { label: "Client", value: "client" },
              { label: "Internal", value: "internal" },
            ]}
          />
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={() => handleOpenModal()}
          >
            Add Note
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      ) : notes.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notes yet"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {notes.map((note: any) => (
            <Card
              key={note.id}
              className={`${note.isPinned ? "border-2 border-yellow-400" : ""}`}
              actions={[
                <Button
                  type="text"
                  size="small"
                  icon={<Pin size={14} className={note.isPinned ? "fill-yellow-500 text-yellow-500" : ""} />}
                  onClick={() => handleTogglePin(note.id)}
                >
                  {note.isPinned ? "Unpin" : "Pin"}
                </Button>,
                <Button
                  type="text"
                  size="small"
                  icon={<Edit2 size={14} />}
                  onClick={() => handleOpenModal(note)}
                >
                  Edit
                </Button>,
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<Trash2 size={14} />}
                  onClick={() => handleDelete(note.id)}
                >
                  Delete
                </Button>,
              ]}
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Tag color={getNoteTypeColor(note.noteType)}>
                    {note.noteType}
                  </Tag>
                  {note.isPinned && (
                    <Pin size={16} className="text-yellow-500 fill-yellow-500" />
                  )}
                </div>
                <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                <div className="text-xs text-gray-500 pt-2 border-t">
                  <p>
                    By {note.author?.firstName} {note.author?.lastName}
                  </p>
                  <p>{dayjs(note.createdAt).fromNow()}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        title={editingNote ? "Edit Note" : "Add Note"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        okText={editingNote ? "Update" : "Create"}
        width={600}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item name="projectId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="noteType"
            label="Note Type"
            rules={[{ required: true, message: "Please select note type" }]}
            initialValue="general"
          >
            <Select
              options={[
                { label: "General", value: "general" },
                { label: "Operational", value: "operational" },
                { label: "Client", value: "client" },
                { label: "Internal", value: "internal" },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="Content"
            rules={[{ required: true, message: "Please enter note content" }]}
          >
            <TextArea rows={6} placeholder="Enter note content..." />
          </Form.Item>

          <Form.Item name="isPinned" valuePropName="checked" initialValue={false}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="cursor-pointer" />
              <span>Pin this note</span>
            </label>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
